import { Container, Rectangle, Sprite, Texture } from 'pixi.js';
import { loadBitmap, type AssetCatalog } from './assets';
import { defaultLayerPaths, FOOT_Y, frameRect, STANDING, type Sex } from './character-sheet';

/** Folhas já pedidas por caminho: jogadores que vestem a mesma peça compartilham o download. */
const sheetCache = new Map<string, Promise<ImageBitmap | null>>();

function sheet(assets: AssetCatalog, path: string): Promise<ImageBitmap | null> {
  let cached = sheetCache.get(path);
  if (!cached) {
    cached = loadBitmap(assets.url(path));
    sheetCache.set(path, cached);
  }
  return cached;
}

/** As folhas de uma aparência já baixadas; cada jogador monta o próprio `Container` a partir dela. */
export class CharacterSkin {
  /** Recortes já criados, por camada e quadro: trocar de quadro a cada tick não aloca texturas. */
  private readonly frames = new Map<string, Texture>();

  private constructor(private readonly sheets: Texture[]) {}

  /** `null` se alguma camada faltar no manifest ou não baixar: o desenho geométrico continua valendo. */
  static async load(assets: AssetCatalog, sex: Sex = 'm', layers: readonly string[] = defaultLayerPaths(sex)): Promise<CharacterSkin | null> {
    const paths = layers;
    if (paths.length === 0 || !paths.every((path) => assets.has(path))) return null;
    const bitmaps = await Promise.all(paths.map((path) => sheet(assets, path)));
    if (bitmaps.some((bitmap) => bitmap === null)) {
      for (const path of paths) sheetCache.delete(path); // falha de rede: tenta de novo na próxima vez
      return null;
    }
    return new CharacterSkin(bitmaps.map((bitmap) => Texture.from(bitmap!)));
  }

  /** Camadas empilhadas (um `Sprite` por folha) com a origem nos pés: a `position` do contêiner é o ponto do chão. */
  create(): Container {
    const root = new Container();
    this.sheets.forEach(() => root.addChild(new Sprite()));
    this.show(root, 0, STANDING.footX);
    return root;
  }

  /** Mostra o quadro em todas as camadas, com os pés em `footX` dentro da célula. */
  show(root: Container, frame: number, footX: number): void {
    root.pivot.set(footX, FOOT_Y);
    this.sheets.forEach((sheet, layer) => {
      (root.children[layer] as Sprite).texture = this.texture(layer, sheet, frame);
    });
  }

  private texture(layer: number, sheet: Texture, frame: number): Texture {
    const key = `${layer}:${frame}`;
    let texture = this.frames.get(key);
    if (!texture) {
      const r = frameRect(frame);
      texture = new Texture({ source: sheet.source, frame: new Rectangle(r.x, r.y, r.width, r.height) });
      this.frames.set(key, texture);
    }
    return texture;
  }
}
